import React, { FC, useRef, useEffect, useCallback, PointerEvent, useState, useMemo, WheelEvent } from 'react';
import clamp from 'lodash/clamp';

import useElementSize from 'src/shared/hooks/useElementSize';

const SCROLL_SENSITIVITY = 0.001;
export const ZOOM_PAN_IMAGE_MIN_SCALE = 1;
export const ZOOM_PAN_IMAGE_MAX_SCALE = 5;

interface Props {
  imageSrc: string;
  width?: number;
  height?: number;
  scale?: number;
  onScaleChange?: (scale: number) => void;
  position?: { x: number; y: number };
  onPositionChange?: (position: { x: number; y: number }) => void;
  borderRadius?: string | number;
  disabled?: boolean;
}

export const ZoomPanImage: FC<Props> = ({
  imageSrc,
  width,
  height,
  scale = 1,
  onScaleChange,
  position = { x: 0, y: 0 },
  onPositionChange,
  borderRadius,
  disabled,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef(new Image());

  const clampedScale = useMemo(() => clamp(scale, ZOOM_PAN_IMAGE_MIN_SCALE, ZOOM_PAN_IMAGE_MAX_SCALE), [scale]);

  const [rootPosition, setRootPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const { width: containerWidth, height: containerHeight } = useElementSize(containerRef);

  const canvasWidth = useMemo(() => width || containerWidth, [width, containerWidth]);
  const canvasHeight = useMemo(() => height || containerHeight, [height, containerHeight]);

  const draw = useCallback(
    (scale = 1, position = { x: 0, y: 0 }) => {
      if (!canvasRef.current || !canvasWidth || !canvasHeight) {
        return;
      }
      const context = canvasRef.current.getContext('2d');

      if (!context) {
        return;
      }

      canvasRef.current.width = canvasWidth;
      canvasRef.current.height = canvasHeight;

      const { width: originalImageWidth, height: originalImageHeight } = imageRef.current;
      const containFitRatio = getContainFitRatio(canvasWidth, canvasHeight, originalImageWidth, originalImageHeight);
      const imageWidth = originalImageWidth * containFitRatio;
      const imageHeight = originalImageHeight * containFitRatio;

      const positionLimitX = Math.abs(canvasWidth / scale - imageWidth) / 2;
      const positionLimitY = Math.abs(canvasHeight / scale - imageHeight) / 2;
      const positionX = clamp(position.x, -1, 1) * positionLimitX;
      const positionY = clamp(position.y, -1, 1) * positionLimitY;

      const containFitOffsetX = Math.max((canvasWidth - imageWidth) / 2, 0);
      const containFitOffsetY = Math.max((canvasHeight - imageHeight) / 2, 0);
      const centerOffsetX = (canvasWidth / scale - canvasWidth) / 2 + containFitOffsetX;
      const centerOffsetY = (canvasHeight / scale - canvasHeight) / 2 + containFitOffsetY;

      context?.clearRect(0, 0, canvasWidth, canvasHeight);
      context?.scale(scale, scale);
      context?.translate(positionX, positionY);
      context?.drawImage(imageRef.current, centerOffsetX, centerOffsetY, imageWidth, imageHeight);
    },
    [canvasWidth, canvasHeight],
  );

  // Redraw canvas on imageSrc change
  useEffect(() => {
    imageRef.current.src = imageSrc;

    imageRef.current.onload = () => {
      draw(clampedScale, position);
    };
  }, [draw, imageSrc]);

  // Redraw canvas on scale & offset change
  useEffect(() => {
    if (!imageRef.current.complete || disabled) {
      return;
    }
    draw(clampedScale, position);
  }, [clampedScale, position, disabled]);

  const handleWheel = (event: WheelEvent<HTMLCanvasElement>) => {
    const { deltaY } = event;
    const newScale = clamp(scale + -deltaY * SCROLL_SENSITIVITY, ZOOM_PAN_IMAGE_MIN_SCALE, ZOOM_PAN_IMAGE_MAX_SCALE);
    onScaleChange?.(newScale);
  };

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      if (disabled) {
        return;
      }
      const { clientX: downX, clientY: downY } = event;
      setRootPosition({ x: downX, y: downY });
      setIsDragging(true);
    },
    [disabled],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      if (!isDragging || !canvasWidth || !canvasHeight || disabled) {
        return;
      }
      const { clientX: pointerX, clientY: pointerY } = event;

      const { x: rootX, y: rootY } = rootPosition;
      const { width: originalImageWidth, height: originalImageHeight } = imageRef.current;

      const containFitRatio = getContainFitRatio(canvasWidth, canvasHeight, originalImageWidth, originalImageHeight);

      const imageWidth = originalImageWidth * containFitRatio;
      const imageHeight = originalImageHeight * containFitRatio;

      const positionLimitX = Math.abs(canvasWidth / clampedScale - imageWidth) / 2;
      const positionLimitY = Math.abs(canvasHeight / clampedScale - imageHeight) / 2;

      const positionX = positionLimitX
        ? clamp(position.x + (pointerX - rootX) / clampedScale / positionLimitX, -1, 1)
        : 0;
      const positionY = positionLimitY
        ? clamp(position.y + (pointerY - rootY) / clampedScale / positionLimitY, -1, 1)
        : 0;

      onPositionChange?.({ x: positionX, y: positionY });
    },
    [isDragging, canvasWidth, canvasHeight, disabled, rootPosition, clampedScale],
  );

  const handlePointerUp = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsDragging(false);
  }, [disabled]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onWheel={handleWheel}
        style={{ cursor: disabled ? '' : 'move', borderRadius }}
      />
    </div>
  );
};

const getContainFitRatio = (canvasWidth: number, canvasHeight: number, imageWidth: number, imageHeight: number) => {
  const widthRatio = canvasWidth / imageWidth;
  const heightRatio = canvasHeight / imageHeight;
  const ratio = Math.min(widthRatio, heightRatio);

  return ratio;
};
